import { Add as AddIcon, PlaylistAdd as PlaylistAddIcon } from "@mui/icons-material";
import { Container, Grid, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import FullScreenDialog from "components/FullScreenDialog";
import { AddExtraDialog } from "components/job/extra/AddExtraDialog";
import { ExtraCard } from "components/job/extra/ExtraCard";
import { StyledFab } from "components/StyledFab";
import { Extra } from "models/extra";
import { TabProps } from "models/TabProps";
import { ExtraInputType } from "operations/schema/schema";
import { FC, useCallback, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import {
  addExtra,
  removeExtra,
  selectSelectedJob,
  selectSelectedJobVisit,
  updateExtra,
} from "store/slices/partner.store";

const PREFIX = "ExtrasTab";

const classes = {
  input: `${PREFIX}-input`,
};

const StyledContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(2),

  [`& .${classes.input}`]: {
    display: "none",
  },
}));

export const ExtrasTab: FC<TabProps> = (props) => {
  const { setFab } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { extras } = useAppSelector(selectSelectedJobVisit);
  const job = useAppSelector(selectSelectedJob);
  const [openAddExtraDialog, setOpenAddExtraDialog] = useState(false);

  useEffect(() => {
    const fab = (
      <StyledFab
        color="primary"
        aria-label={intl.formatMessage({ id: "general.add" })}
        onClick={() => {
          setOpenAddExtraDialog(true);
        }}
        data-testid="PartTabAddButton"
      >
        <AddIcon />
      </StyledFab>
    );
    if (setFab) {
      setFab(fab);
    }
  }, [intl, setFab]);

  const onRemove = useCallback(
    (index: number) => {
      dispatch(removeExtra({ index: index }));
    },
    [dispatch]
  );

  const onUpdate = useCallback(
    (extra: Extra, index: number) => {
      dispatch(updateExtra({ extra: extra, index: index }));
    },
    [dispatch]
  );

  const onAdd = useCallback(
    (extra: ExtraInputType) => {
      dispatch(addExtra({ extra }));
    },
    [dispatch]
  );

  return (
    <StyledContainer data-testid="ExtrasTab">
      {extras.length === 0 && (
        <Grid container direction="column" alignItems="center">
          <Grid item>
            <PlaylistAddIcon color="secondary" fontSize="large" />
          </Grid>
          <Grid item>
            <Typography color="secondary">
              <FormattedMessage id="visit.noExtrasAdded" />
            </Typography>
          </Grid>
        </Grid>
      )}
      <Grid container spacing={1}>
        {extras.map((e, index) => (
          <ExtraCard
            key={index}
            extra={e}
            index={index}
            disabled={(e.uploaded || e.pendingUpload) ?? false}
            onRemove={onRemove}
            onUpdate={onUpdate}
          />
        ))}
      </Grid>
      <FullScreenDialog
        child={
          <AddExtraDialog
            setOpenAddExtraDialog={setOpenAddExtraDialog}
            loading={false}
            onAddExtra={onAdd}
          />
        }
        title={`${job?.externalId} / ${intl.formatMessage({ id: "job.extras" })}`}
        centerTitle
        goingBack
        open={openAddExtraDialog}
        setOpen={(open: boolean) => setOpenAddExtraDialog(open)}
      />
    </StyledContainer>
  );
};
